import React, { Component } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { queryTimestreamData } from '../services/timestream';

class VehicleChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: [],
      loading: false,
      error: null,
      startTimeISO: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      endTimeISO: new Date().toISOString(),
    };
    this.chartRef = React.createRef();
  
  }

  componentDidMount() {
    if (Array.isArray(this.props.initialData) && this.props.initialData.length > 0) {
      this.setState({ chartData: this.props.initialData, loading: false, error: null });
    } else if (this.props.selectedDeviceId && !this.props.skipInitialFetch) {
      this.loadChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialData !== prevProps.initialData && Array.isArray(this.props.initialData)) {
      this.setState({ chartData: this.props.initialData, loading: false, error: null });
      return;
    }
    if (prevProps.selectedDeviceId !== this.props.selectedDeviceId && this.props.selectedDeviceId && !this.props.skipInitialFetch) {
      this.loadChartData();
    }
    if (prevProps.hoverTimestamp !== this.props.hoverTimestamp) {
      this.updateHoverCursor(this.props.hoverTimestamp);
    }
  }

  loadChartData = async () => {
    if (!this.props.selectedDeviceId) return;

    this.setState({ loading: true, error: null });

    try {
      const endTime = this.state.endTimeISO;
      const startTime = this.state.startTimeISO;

      const data = await queryTimestreamData(
        this.props.selectedDeviceId || this.props.selectedVehicle,
        startTime,
        endTime
      );
      const measures = Array.isArray(data) ? data : (Array.isArray(data?.measures) ? data.measures : []);
      this.setState({ chartData: measures, loading: false });
    } catch (error) {
      console.error('Error loading chart data:', error);
      this.setState({ error: 'Failed to load chart data', loading: false });
    }
  };

  formatLocalInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  onStartChange = (e) => {
    const v = e.target.value;
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) {
      this.setState({ startTimeISO: dt.toISOString() });
    }
  };

  onEndChange = (e) => {
    const v = e.target.value;
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) {
      this.setState({ endTimeISO: dt.toISOString() });
    }
  };

  triggerUpdate = () => {
    if (typeof this.props.onUpdateRange === 'function') {
      this.props.onUpdateRange(this.state.startTimeISO, this.state.endTimeISO);
    } else {
      this.loadChartData();
    }
  };

  updateHoverCursor = (tsSec) => {
    const chart = this.chartRef && this.chartRef.current && this.chartRef.current.chart ? this.chartRef.current.chart : null;
    if (!chart || !chart.xAxis || !chart.xAxis[0]) return;
    const id = 'hover-line';
    try { chart.xAxis[0].removePlotLine(id); } catch(e){}
    if (!tsSec && tsSec !== 0) {
      try { chart.tooltip.hide(0); } catch(e){}
      chart.redraw();
      return;
    }
    const x = tsSec * 1000;
    chart.xAxis[0].addPlotLine({ value: x, color: '#888', width: 1, id });
    const points = (chart.series || []).map(s => {
      const arr = s.points || [];
      let best = null, diff = Infinity;
      for (let i = 0; i < arr.length; i++) {
        const d = Math.abs(arr[i].x - x);
        if (d < diff) { diff = d; best = arr[i]; }
      }
      return best;
    }).filter(Boolean);
    if (points.length) {
      try { chart.tooltip.refresh(points); } catch(e){}
    }
  };

  getTempStats = () => {
    const temps = this.state.chartData
      .filter(item => /temp|temperature|value/i.test(item.measureName) && typeof item.value === 'number' && item.value >= -30)
      .map(item => item.value);
    if (!temps.length) return null;
    const min = Math.min(...temps);
    const max = Math.max(...temps);
    const mean = temps.reduce((a, b) => a + b, 0) / temps.length;
    return { min, max, mean, count: temps.length };
  };

  setQuickRangeHours = (hrs) => {
    const end = new Date();
    const start = new Date(Date.now() - hrs * 60 * 60 * 1000);
    this.setState({ startTimeISO: start.toISOString(), endTimeISO: end.toISOString() }, () => {
      this.triggerUpdate();
    });
  };

  getChartOptions = () => {
    const { chartData } = this.state;
    const onHoverTs = this.props.onHoverTimestamp;
    // Mantém o que o usuário ligou/desligou na legenda quando as opções são
    // regeradas (ex.: ponto novo chegando via MQTT)
    const chart = this.chartRef && this.chartRef.current ? this.chartRef.current.chart : null;
    const visibleByName = {};
    (chart && chart.series ? chart.series : []).forEach((s) => { visibleByName[s.name] = s.visible; });
    const isVisible = (name, def) => (name in visibleByName ? visibleByName[name] : def);
    
    const groupedData = chartData.reduce((acc, item) => {
      if (!acc[item.measureName]) {
        acc[item.measureName] = [];
      }
      const tRaw = item.timestamp !== undefined ? item.timestamp : item.time;
      let tMs;
      if (typeof tRaw === 'number') {
        tMs = tRaw < 1e11 ? tRaw * 1000 : tRaw;
      } else if (typeof tRaw === 'string') {
        tMs = new Date(tRaw).getTime();
      }
      if (Number.isFinite(tMs)) {
        acc[item.measureName].push([tMs, item.value]);
      }
      return acc;
    }, {});

    const series = Object.keys(groupedData).map((measureName, index) => {
      const data = groupedData[measureName].sort((a, b) => a[0] - b[0]);
      const isTemp = /temp|temperature|value/i.test(measureName);
      const isVolt = /volt|voltage/i.test(measureName);
      const isVel = /velocity|speed|kmh|km\/h/i.test(measureName);
      if (isTemp) {
        const filtered = data.filter((pt) => !(typeof pt[1] === 'number' && pt[1] < -30));
        return {
          name: measureName,
          data: filtered,
          zoneAxis: 'y',
          zones: [
            { value: 0, color: '#4486E8' },
            { value: 10, color: '#FFC107' },
            { color: '#F52C16' }
          ]
        };
      }
      if (isVolt) {
        const sample = data.length ? data[Math.floor(data.length / 2)][1] : 0;
        const useMv = sample > 20;
        const low = useMv ? 3400 : 3.4;
        const mid = useMv ? 3700 : 3.7;
        return {
          name: measureName,
          data,
          zoneAxis: 'y',
          zones: [
            { value: low, color: '#F52C16' },
            { value: mid, color: '#FFC107' },
            { color: '#2ECC71' }
          ],
          visible: isVisible(measureName, false)
        };
      }
      if (isVel) {
        return {
          name: measureName,
          data,
          color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][index % 5],
          visible: isVisible(measureName, false)
        };
      }
      return {
        name: measureName,
        data,
        color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][index % 5],
      };
    });

    return {
      time: { useUTC: false },
      chart: {
        type: 'line',
        height: 300,
        backgroundColor: '#fff',
      },
      title: {
        text: `Série histórica - ${this.props.selectedVehicle}`,
        style: { fontSize: '16px' },
      },
      xAxis: {
        type: 'datetime',
        title: { text: 'Tempo' }
      },
      yAxis: {
        title: { text: 'Value' },
      },
      legend: {
        enabled: true,
        align: 'bottom',
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
            states: {
              hover: { enabled: true },
            },
          },
        },
        series: {
          point: {
            events: {
              mouseOver: function() {
                try {
                  if (typeof onHoverTs === 'function') {
                    const tsSec = Math.floor(this.x / 1000);
                    onHoverTs(tsSec);
                  }
                } catch(e){}
              },
              mouseOut: function() {
                try {
                  if (typeof onHoverTs === 'function') onHoverTs(undefined);
                } catch(e){}
              }
            }
          }
        }
      },
      series: series,
      tooltip: {
        shared: true,
        crosshairs: true,
        formatter: function() {
          const dt = new Intl.DateTimeFormat('pt-BR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
          }).format(new Date(this.x));
          let tooltip = `<b>${dt}</b><br/>`;
          this.points.forEach(point => {
            tooltip += `<span style=\"color:${point.color}\">${point.series.name}</span>: <b>${point.y.toFixed(2)}</b><br/>`;
          });
          return tooltip;
        },
      },
    };
  };

  renderToolbar = () => {
    const { loading } = this.state;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <label style={{ fontSize: 12 }}>Início:</label>
        <input
          type="datetime-local"
          value={this.formatLocalInput(this.state.startTimeISO)}
          onChange={this.onStartChange}
          style={{ fontSize: 12, padding: '2px 6px' }}
        />
        <label style={{ fontSize: 12 }}>Fim:</label>
        <input
          type="datetime-local"
          value={this.formatLocalInput(this.state.endTimeISO)}
          onChange={this.onEndChange}
          style={{ fontSize: 12, padding: '2px 6px' }}
        />
        <button
          onClick={this.triggerUpdate}
          disabled={loading}
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#f4f4f4' }}
        >Atualizar</button>
        <button
          onClick={this.props.onClearPolylines}
          style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#f4f4f4' }}
        >Limpar Trajeto</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={() => this.setQuickRangeHours(1)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#fafafa' }}>1h</button>
          <button onClick={() => this.setQuickRangeHours(6)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#fafafa' }}>6h</button>
          <button onClick={() => this.setQuickRangeHours(24)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#fafafa' }}>24h</button>
        </div>
      </div>
    );
  };

  renderMessage = (text, color) => (
    <div style={{
      height: 300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: 6,
    }}>
      <p style={{ color, margin: 0 }}>{text}</p>
    </div>
  );

  render() {
    const { selectedVehicle } = this.props;
    const { loading, error, chartData } = this.state;

    if (!selectedVehicle) {
      return this.renderMessage('Select a vehicle from the table to view its data', '#6c757d');
    }

    // Toolbar stays available even with no data in range, so an old period can be queried
    let body;
    if (loading) {
      body = this.renderMessage('Loading chart data...', '#6c757d');
    } else if (error) {
      body = this.renderMessage(error, '#dc3545');
    } else if (chartData.length === 0) {
      body = this.renderMessage(`Sem dados para ${selectedVehicle} no período selecionado`, '#6c757d');
    }

    if (body) {
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          padding: 10,
        }}>
          {this.renderToolbar()}
          {body}
        </div>
      );
    }

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: 6,
        padding: 10,
      }}>
        {this.renderToolbar()}
        <HighchartsReact
          highcharts={Highcharts}
          options={this.getChartOptions()}
          ref={this.chartRef}
        />
        {(() => {
          const stats = this.getTempStats();
          if (!stats) return null;
          const cell = (label, value, color) => (
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 11, color: '#888' }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color }}>{value.toFixed(2)} °C</div>
            </div>
          );
          return (
            <div style={{ display: 'flex', justifyContent: 'space-around', padding: '8px 0 2px', borderTop: '1px solid #eee', marginTop: 4 }}>
              {cell('Mínima', stats.min, '#4486E8')}
              {cell('Média', stats.mean, '#FFC107')}
              {cell('Máxima', stats.max, '#F52C16')}
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 11, color: '#888' }}>Num. Pontos</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#555' }}>{stats.count}</div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
}

export default VehicleChart;